import { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { OrderCard } from '../../components/nursery';
import {
  Plus, Package, TreePine, ShoppingCart, ClipboardList, Boxes,
  Warehouse, MapPinned, Users, Mail, Sprout, Briefcase, Truck, PackageCheck, Route as RouteIcon, Container, ArrowRightLeft, RefreshCw,
  Tags, FileUp, CalendarClock,
} from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, getTodayInTz } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { AlertTriangle } from 'lucide-react';
import type { NurseryDashboardStatsV2, NurseryOrder } from '../../types/nursery';

// Invoices, receivables, rebates, price lists and Xero are the Accounts
// module's (/accounts). Customers stay here to read: an order is taken for one.
const QUICK_LINKS = [
  { label: 'Production',  icon: ClipboardList, path: '/nursery/production' },
  { label: 'Lots',        icon: Boxes,         path: '/nursery/lots' },
  { label: 'Locations',   icon: MapPinned,     path: '/nursery/locations' },
  { label: 'Tree Types',  icon: Sprout,        path: '/nursery/tree-types' },
  { label: 'Stock Items', icon: Package,       path: '/nursery/stock-items' },
  { label: 'Products',    icon: Tags,          path: '/nursery/products' },
  { label: 'Orders',      icon: ShoppingCart,  path: '/nursery/orders' },
  { label: 'Import POs',  icon: FileUp,        path: '/nursery/orders/import' },
  { label: 'Cross Dock',  icon: CalendarClock, path: '/nursery/cross-dock' },
  { label: 'Despatch',    icon: RouteIcon,     path: '/nursery/despatch' },
  { label: 'Loads',       icon: Container,     path: '/nursery/loads' },
  { label: 'Picking',     icon: PackageCheck,  path: '/nursery/picking' },
  { label: 'Transfers',   icon: ArrowRightLeft, path: '/nursery/transfers' },
  { label: 'Despatch Areas', icon: RefreshCw,  path: '/nursery/refills' },
  { label: 'Customers',   icon: Users,         path: '/accounts/customers' },
  { label: 'Website Signups', icon: Mail,      path: '/nursery/website-signups' },
  { label: 'Trade Requests', icon: Briefcase,  path: '/nursery/trade-account-requests' },
  { label: 'Delivery Zones', icon: Truck,      path: '/nursery/delivery-zones' },
  { label: 'Order Pickers', icon: PackageCheck, path: '/nursery/pickers' },
  { label: 'Inventory',   icon: Warehouse,     path: '/nursery/inventory' },
];

function NurseryDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<NurseryDashboardStatsV2 | null>(null);
  const [recentOrders, setRecentOrders] = useState<NurseryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Orders due in the next 30 days that nobody has put on a load. Managers
  // plan loads, so only they are asked; the planner is gated the same way.
  const tz = useOrgTimezone();
  const { hasAccess: canPlan } = useModuleLicense('nursery', ['manager', 'admin']);
  const [unloaded, setUnloaded] = useState<{ orders: number; overdue: number } | null>(null);
  useEffect(() => {
    if (!canPlan) return;
    const today = getTodayInTz(tz);
    nurseryService.getDespatchPlanner(today, addDays(today, 30))
      .then((res) => {
        if (res.success) setUnloaded({ orders: res.data.summary.not_on_load, overdue: res.data.summary.overdue_not_on_load });
      })
      .catch(() => { /* the planner page will say what is wrong */ });
  }, [canPlan, tz]);

  // Someone holding only the Order Picker seat has nothing here but picking,
  // and every read below would be refused. Send them straight to their loads.
  const { hasAccess: canOperate, loading: seatLoading } = useModuleLicense('nursery', ['operator', 'manager', 'admin']);

  useEffect(() => {
    if (!canOperate) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, ordersRes] = await Promise.all([
          nurseryService.getDashboard(),
          nurseryService.getOrders({ status: undefined }),
        ]);
        if (statsRes.success) setStats(statsRes.data);
        if (ordersRes.success) setRecentOrders((ordersRes.data || []).slice(0, 5));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [canOperate]);

  const statCards = stats
    ? [
        { label: 'Active Lots',     value: stats.active_lots,  color: 'bg-green-50 text-green-700' },
        { label: 'Total Trees',     value: stats.total_trees,  color: 'bg-teal-50 text-teal-700' },
        { label: 'Ready Now',       value: stats.ready_now,    color: 'bg-emerald-50 text-emerald-700' },
        { label: 'Ready ≤30 days',  value: stats.ready_30d,    color: 'bg-amber-50 text-amber-700' },
        { label: 'Ready ≤90 days',  value: stats.ready_90d,    color: 'bg-blue-50 text-blue-700' },
      ]
    : [];

  if (!seatLoading && !canOperate) return <Navigate to="/nursery/picking" replace />;

  return (
    <>
      <PageHeader
        title="Nursery Management"
        subtitle="Lots, locations, production & orders"
        icon={<TreePine className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" onClick={() => navigate('/nursery/lots/new')}>
              <Plus className="h-4 w-4" />
              Place Lot
            </Button>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {unloaded && unloaded.orders > 0 && (
          <Link to="/nursery/despatch"
            className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 mb-6 hover:border-amber-300">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
            <span>
              <span className="font-semibold">
                {unloaded.orders} order{unloaded.orders === 1 ? '' : 's'} due in the next 30 days {unloaded.orders === 1 ? 'is' : 'are'} not on a load
              </span>
              {unloaded.overdue > 0 && <span className="text-red-700">, {unloaded.overdue} already overdue</span>}
              . Open the despatch planner.
            </span>
          </Link>
        )}

        {/* Stat cards */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-24 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {statCards.map(card => (
              <div
                key={card.label}
                className={`rounded-xl border border-neutral-200 p-4 ${card.color}`}
              >
                <p className="text-2xl font-bold">{card.value.toLocaleString()}</p>
                <p className="text-xs font-medium mt-1 opacity-80">{card.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        {/* Quick links grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {QUICK_LINKS.map(link => {
            const Icon = link.icon;
            return (
              <Link
                key={link.path}
                to={link.path}
                className="flex flex-col items-center gap-2 p-4 bg-white border border-neutral-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all text-center"
              >
                <Icon className="h-6 w-6 text-primary-600" />
                <span className="text-sm font-medium text-neutral-700">{link.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Recent orders */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Recent Orders</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate('/nursery/orders')}>
              View All
            </Button>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 bg-neutral-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              <ShoppingCart className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrders.map(order => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default NurseryDashboard;
